import { useEffect, useState } from 'react';
import { DEFAULT_GAME_ID } from '../../../shared/domain.ts';
import type { GameId } from '../../../shared/domain.ts';
import { fetchLeaderboard } from '../../lib/api.ts';
import type { LeaderboardEntry, Me } from '../../lib/api.ts';
import { GamePicker } from './GamePicker.tsx';
import { EmailForm } from '../auth/EmailForm.tsx';
import { NameEditor } from '../auth/NameEditor.tsx';
import type { MatchView } from '../match/useMatch.ts';
import { HostPanel } from './HostPanel.tsx';
import { JoinByCode } from './JoinByCode.tsx';

export interface LobbyPageProps {
  readonly me: Me | null;
  readonly view: MatchView;
  readonly onQueue: (game: GameId) => void;
  readonly onLeaveQueue: () => void;
  readonly onHostLobby: (game: GameId) => void;
  readonly onJoinLobby: (code: string) => void;
  readonly onCancelLobby: () => void;
  readonly onMoveWaiter: (userId: string, direction: 'up' | 'down') => void;
  readonly onStartWith: (userId: string) => void;
  readonly onKickWaiter: (userId: string) => void;
  /** Opens the local practice game. Needs no socket, so it is never disabled. */
  readonly onPractise: () => void;
  /** Opens the local puzzle run. Needs no socket either. */
  readonly onSolvePuzzles: () => void;
  /** Opens today's puzzle. Local too. */
  readonly onDailyPuzzle: () => void;
  /** Opens the lessons. Local too. */
  readonly onLearn: () => void;
  /** Opens the parity counting trainer. Local too. */
  readonly onCountParity: () => void;
  readonly onPlayAsGuest: () => void;
  readonly startingGuest: boolean;
  readonly onSignedIn: (me: Me) => void;
  readonly onRename: (name: string) => void;
}

type LeaderboardState =
  | { status: 'loading' }
  | { status: 'ready'; entries: LeaderboardEntry[] }
  | { status: 'error' };

function Leaderboard() {
  const [state, setState] = useState<LeaderboardState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    fetchLeaderboard(controller.signal)
      .then((entries) => setState({ status: 'ready', entries }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.error('leaderboard failed', error);
        setState({ status: 'error' });
      });
    return () => controller.abort();
  }, []);

  if (state.status === 'loading') {
    return (
      <p className="panel__empty" aria-live="polite">
        Loading ratings.
      </p>
    );
  }

  if (state.status === 'error') {
    return <p className="panel__empty">Ratings are unavailable right now.</p>;
  }

  if (state.entries.length === 0) {
    // Honest empty state: no invented players, no placeholder rows.
    return <p className="panel__empty">No rated games yet. The first match sets the board.</p>;
  }

  return (
    <table className="ladder">
      <caption className="visually-hidden">Top rated players</caption>
      <thead>
        <tr>
          <th scope="col" className="ladder__rank">
            #
          </th>
          <th scope="col">Player</th>
          <th scope="col" className="ladder__number">
            Rating
          </th>
          <th scope="col" className="ladder__number">
            W/L/D
          </th>
        </tr>
      </thead>
      <tbody>
        {state.entries.map((entry, index) => (
          <tr key={entry.userId}>
            <td className="ladder__rank">{index + 1}</td>
            <td className="ladder__player">{entry.displayName}</td>
            <td className="ladder__number">{entry.rating}</td>
            <td className="ladder__number">
              {entry.wins}/{entry.losses}/{entry.draws}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function LobbyPage({
  me,
  view,
  onQueue,
  onLeaveQueue,
  onHostLobby,
  onJoinLobby,
  onCancelLobby,
  onMoveWaiter,
  onStartWith,
  onKickWaiter,
  onPractise,
  onSolvePuzzles,
  onDailyPuzzle,
  onLearn,
  onCountParity,
  onPlayAsGuest,
  startingGuest,
  onSignedIn,
  onRename,
}: LobbyPageProps) {
  const connecting = view.connection === 'reconnecting' || view.phase === 'connecting';

  // Which game the next queue or host will start. Local to the lobby: it is a
  // pre-match choice, and the match's own game arrives from the server.
  const [game, setGame] = useState<GameId>(DEFAULT_GAME_ID);

  // Hosting takes over the intro entirely: while a QR is on screen it is the
  // only thing the streamer and their audience should be looking at.
  if (me && view.phase === 'hosting' && view.lobby) {
    return (
      <section className="lobby" aria-label="Lobby">
        <div className="lobby__intro">
          <HostPanel
            lobby={view.lobby}
            onCancel={onCancelLobby}
            hostName={me.displayName}
            onRename={onRename}
            onMoveWaiter={onMoveWaiter}
            onStartWith={onStartWith}
            onKickWaiter={onKickWaiter}
          />
        </div>

        {/* Same panel chrome as the idle lobby, so hosting does not look like a
            different application. */}
        <aside className="panel" aria-labelledby="ladder-heading-hosting">
          <h3 className="panel__heading" id="ladder-heading-hosting">
            Ratings
          </h3>
          <Leaderboard />
        </aside>
      </section>
    );
  }

  return (
    <section className="lobby" aria-label="Lobby">
      <div className="lobby__intro">
        <h2 className="lobby__heading">Connect 4</h2>
        <p className="lobby__lede">
          Seven columns, six rows, four in a row. Every move is recorded, so ratings come straight
          from the games you actually played.
        </p>

        {!me ? (
          /*
           * Playing is free and needs no account, so the guest button is the
           * primary action and sign-in is tucked below it. Leading with a login
           * would imply an account is required, which is the exact thing that
           * loses a viewer who just scanned a QR mid-stream.
           */
          <div className="lobby__cta">
            <button
              type="button"
              className="button button--primary button--large"
              onClick={onPlayAsGuest}
              disabled={startingGuest}
            >
              {startingGuest ? 'Starting' : 'Play now'}
            </button>
            <p className="lobby__note">No account needed. You can sign in later and keep your games.</p>

            <p className="lobby__note">
              Signing in saves your rating across devices. It confirms who you are and nothing
              else — no extra permissions are requested.
            </p>
            <EmailForm onSignedIn={onSignedIn} claiming={false} />
          </div>
        ) : view.phase === 'queued' ? (
          <div className="lobby__cta">
            <p className="lobby__queued" aria-live="polite">
              <span className="spinner" aria-hidden="true" />
              Looking for an opponent
            </p>
            <button type="button" className="button button--quiet" onClick={onLeaveQueue}>
              Leave queue
            </button>
          </div>
        ) : (
          <div className="lobby__cta">
            {/*
              Name first, above everything it affects.

              It used to sit below Host and Join, collapsed behind a button, so
              the last chance to set a name came before anyone had thought about
              hosting — and once the QR was up it was unreachable. It is the
              label opponents and a host's queue read, so it leads.
            */}
            <NameEditor currentName={me.displayName} onRename={onRename} />

            {/* Above the actions, because it changes what both of them do. */}
            <GamePicker value={game} onChange={setGame} disabled={connecting} />

            <button
              type="button"
              className="button button--primary button--large"
              onClick={() => onQueue(game)}
              disabled={connecting}
            >
              {connecting ? 'Connecting' : 'Find a match'}
            </button>

            {/*
              The streamer path. Visually secondary to the public queue, since
              most visitors want a random opponent — but it is the flow a host
              uses on every broadcast.
            */}
            <button
              type="button"
              className="button button--secondary"
              onClick={() => onHostLobby(game)}
              disabled={connecting}
            >
              Host a game
            </button>

            <JoinByCode onJoin={onJoinLobby} disabled={connecting} />

            {/*
              Practice needs no opponent and no connection: the engine runs in
              this tab. Offered here because the commonest reason to bounce off
              a lobby is that there is nothing to do while the queue is empty.
            */}
            <button type="button" className="button button--quiet" onClick={onPractise}>
              Practise against the coach
            </button>

            {/*
              The other half of practice: a full game teaches you to play, a
              forced win teaches you to finish. Same placement reasoning — both
              are things to do when the queue is empty.
            */}
            {/* First of the three, because it is the one with a reason to come
                back tomorrow: everybody gets the same board today. */}
            <button type="button" className="button button--quiet" onClick={onDailyPuzzle}>
              Puzzle of the day
            </button>

            <button type="button" className="button button--quiet" onClick={onSolvePuzzles}>
              Solve puzzles
            </button>

            {/*
              Named ideas rather than positions. The coach and the puzzles both
              hand you facts about a board; this is the one that gives the thing
              a name you can go looking for during a real game.
            */}
            <button type="button" className="button button--quiet" onClick={onLearn}>
              Learn the ideas
            </button>

            {/*
              Last, because it is the only one that asks you to do arithmetic
              rather than to look at a board — and it is the thing that decides
              games between two players who can both already see a fork.
            */}
            <button type="button" className="button button--quiet" onClick={onCountParity}>
              Count the parity
            </button>

            <p className="lobby__note">
              You are playing as <strong>{me.displayName}</strong>, rated {me.rating}.
            </p>

            {/*
              Guests are rated exactly like anyone else; what they lack is a
              durable identity. Say precisely that, rather than nagging.
            */}
            {me.isGuest && (
              <div className="lobby__note lobby__note--guest">
                <p>
                  You are playing as a guest, so this rating lives in this browser only. Sign in
                  below to keep it — your games so far come with you.
                </p>
                <EmailForm onSignedIn={onSignedIn} claiming />
              </div>
            )}
          </div>
        )}
      </div>

      <aside className="panel" aria-labelledby="ladder-heading">
        <h3 className="panel__heading" id="ladder-heading">
          Ratings
        </h3>
        <Leaderboard />
      </aside>
    </section>
  );
}
